import { useState } from 'react'
import { useBonusConfigs, useCreateBonusConfig, useUpdateBonusConfig, useDeleteBonusConfig, useBackfillBonusConfig, useArchiveBonusConfig, type BonusConfigWithDetails } from '@/hooks/use-bonuses'
import { useGoals } from '@/hooks/use-goals'
import { useUsers } from '@/hooks/use-users'
import { Modal } from '@/components/modal'
import { BonusConfigCard } from '@/components/BonusConfigCard'
import { InstanceDetailModal } from '@/components/InstanceDetailModal'
import { TIMEFRAME_LABELS, type Timeframe } from '@/types'

interface BonusFormData {
  goal_id: string
  user_id: string
  amount: number
  currency: string
}

const defaultFormData: BonusFormData = {
  goal_id: '',
  user_id: '',
  amount: 0,
  currency: 'USD',
}

export function AdminBonuses() {
  const [activeTab, setActiveTab] = useState<'active' | 'archived'>('active')
  const { data: activeConfigs, isLoading: isLoadingActive } = useBonusConfigs({ archived: false })
  const { data: archivedConfigs, isLoading: isLoadingArchived } = useBonusConfigs({ archived: true })
  const { data: goals } = useGoals()
  const { data: users } = useUsers()
  const createBonusConfig = useCreateBonusConfig()
  const updateBonusConfig = useUpdateBonusConfig()
  const deleteBonusConfig = useDeleteBonusConfig()
  const backfillBonusConfig = useBackfillBonusConfig()
  const archiveBonusConfig = useArchiveBonusConfig()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingConfig, setEditingConfig] = useState<BonusConfigWithDetails | null>(null)
  const [formData, setFormData] = useState<BonusFormData>(defaultFormData)
  const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(null)
  const [backfillingConfigId, setBackfillingConfigId] = useState<string | null>(null)
  const [archivingConfigId, setArchivingConfigId] = useState<string | null>(null)

  const configs = activeTab === 'active' ? activeConfigs : archivedConfigs
  const isLoading = activeTab === 'active' ? isLoadingActive : isLoadingArchived

  const openCreate = () => {
    setEditingConfig(null)
    setFormData(defaultFormData)
    setIsModalOpen(true)
  }

  const openEdit = (config: BonusConfigWithDetails) => {
    setEditingConfig(config)
    setFormData({
      goal_id: config.goal_id,
      user_id: config.user_id,
      amount: config.amount,
      currency: config.currency,
    })
    setIsModalOpen(true)
  }

  const handleDelete = async (configId: string) => {
    try {
      await deleteBonusConfig.mutateAsync(configId)
    } catch (err) {
      const error = err as Error & { suggest_archive?: boolean }
      if (error.suggest_archive) {
        alert(`${error.message}\n\nUse the archive button (📦) instead.`)
      } else {
        alert(`Delete failed: ${error.message}`)
      }
    }
  }

  const handleArchive = async (configId: string) => {
    setArchivingConfigId(configId)
    try {
      await archiveBonusConfig.mutateAsync(configId)
    } catch (err) {
      alert(`Archive failed: ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      setArchivingConfigId(null)
    }
  }

  const handleBackfill = async (configId: string) => {
    setBackfillingConfigId(configId)
    try {
      const result = await backfillBonusConfig.mutateAsync(configId)
      alert(`Backfill complete: ${result.instances_created} instances created, ${result.periods_skipped} skipped`)
    } catch (err) {
      alert(`Backfill failed: ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      setBackfillingConfigId(null)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (editingConfig) {
      await updateBonusConfig.mutateAsync({
        id: editingConfig.id,
        amount: formData.amount,
        currency: formData.currency,
      })
    } else {
      await createBonusConfig.mutateAsync(formData)
    }
    setIsModalOpen(false)
    setEditingConfig(null)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal" />
      </div>
    )
  }

  const isEditing = !!editingConfig
  const isPending = createBonusConfig.isPending || updateBonusConfig.isPending

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage Bonuses</h1>
          <p className="text-sm text-gray-500 mt-1">Configure bonuses tied to goal achievements</p>
        </div>
        <button onClick={openCreate} className="btn btn-primary text-sm px-3 py-1.5">
          + Add Bonus Config
        </button>
      </div>

      {/* Tabs: Active | Archived */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-4">
          <button
            onClick={() => setActiveTab('active')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'active'
                ? 'border-brand-teal text-brand-teal'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Active
            {activeConfigs && activeConfigs.length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                {activeConfigs.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('archived')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'archived'
                ? 'border-brand-teal text-brand-teal'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Archived
            {archivedConfigs && archivedConfigs.length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                {archivedConfigs.length}
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* Bonus Config Cards */}
      <div className="space-y-4">
        {configs?.map((config) => (
          <BonusConfigCard
            key={config.id}
            config={config}
            onInstanceClick={setSelectedInstanceId}
            onEdit={openEdit}
            onDelete={handleDelete}
            onBackfill={handleBackfill}
            onArchive={activeTab === 'active' ? handleArchive : undefined}
            isBackfilling={backfillingConfigId === config.id}
            isArchiving={archivingConfigId === config.id}
            isArchived={activeTab === 'archived'}
          />
        ))}
        {(!configs || configs.length === 0) && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
            {activeTab === 'active'
              ? 'No active bonus configurations. Create one to get started.'
              : 'No archived bonus configurations.'}
          </div>
        )}
      </div>

      {/* Info box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-900 mb-2">Bonus Rules</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>Bonus configs define the bonus amount for a user+goal combination</li>
          <li>Recurring bonuses create period instances automatically</li>
          <li>Click to expand and view period history</li>
          <li>HR webhook is triggered when bonus period is met and dispatched</li>
        </ul>
      </div>

      {/* Create/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={isEditing ? 'Edit Bonus Config' : 'Create Bonus Config'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Goal *</label>
            <select
              required
              disabled={isEditing}
              value={formData.goal_id}
              onChange={(e) => setFormData({ ...formData, goal_id: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">Select goal...</option>
              {goals?.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.metric_name} ({TIMEFRAME_LABELS[g.timeframe as Timeframe]})
                </option>
              ))}
            </select>
            {isEditing && <p className="text-xs text-gray-500 mt-1">Goal cannot be changed after creation</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">User *</label>
            <select
              required
              disabled={isEditing}
              value={formData.user_id}
              onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">Select user...</option>
              {users?.filter((u) => u.role !== 'new_user').map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
            {isEditing && <p className="text-xs text-gray-500 mt-1">User cannot be changed after creation</p>}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                required
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              >
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
                <option value="GBP">GBP (£)</option>
              </select>
            </div>
          </div>
          <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
            Bonus periods follow the goal's timeframe (weekly, monthly, etc.)
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={() => setIsModalOpen(false)} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isPending}>
              {isPending ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Bonus Config'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Instance Detail Modal */}
      <InstanceDetailModal
        instanceId={selectedInstanceId}
        isOpen={!!selectedInstanceId}
        onClose={() => setSelectedInstanceId(null)}
      />
    </div>
  )
}
